// Pure helpers + types for deliverable form state. Same pattern as
// insurance/insuranceFormState.ts — keeps non-component exports out of
// the Fields component file so react-refresh rules stay happy.

import type {
  CreateDeliverableInput,
  Deliverable,
  DeliverableLifecycleEventReplaceInput,
  DeliverableOwner,
  DeliverableStatus,
  DeliverableType,
  EvidenceLink,
  RagRating,
  RecurrenceRule,
  UpdateDeliverableInput,
} from "../../../../types";

export interface DeliverableFormState {
  name: string;
  deliverableType: DeliverableType;
  owner: DeliverableOwner;
  description: string;
  clauseReference: string;
  milestoneId: string;
  /** SOW attachment. `null` is the explicit `(none)` selection; on
   *  Edit, transitioning a previously-attached deliverable to `null`
   *  fires an explicit-null clear in the Update payload. On Create,
   *  `null` is omitted from the payload (helper drops it). */
  sowId: string | null;
  dueDate: string;
  ragRating: RagRating | "";
  recurrenceRule: RecurrenceRule | undefined;
  status: DeliverableStatus;
  evidenceLinks: EvidenceLink[];
  notes: string;
  /** Working replacement for the lifecycle events array. Present only when
   *  the lifecycle log editor fieldset is active (edit mode, non-recurring).
   *  When present, the save path sends lifecycleEventsReplace instead of
   *  lifecycleEvents (delta append). Backend validates the full chain. */
  lifecycleEventsReplace?: DeliverableLifecycleEventReplaceInput[];
}

export type DeliverableFieldErrors = Partial<
  Record<keyof DeliverableFormState | "form" | "recurrenceStartDate" | "recurrenceEndDate" | "lifecycleChain", string>
>;

export function buildEmptyDeliverableFormState(): DeliverableFormState {
  return {
    name: "",
    deliverableType: "deliverable",
    owner: "supplier",
    description: "",
    clauseReference: "",
    milestoneId: "",
    sowId: null,
    dueDate: "",
    ragRating: "",
    recurrenceRule: undefined,
    status: "not-started",
    evidenceLinks: [],
    notes: "",
  };
}

export function stateFromDeliverable(item: Deliverable): DeliverableFormState {
  return {
    name: item.name,
    deliverableType: item.deliverableType,
    owner: item.owner,
    description: item.description ?? "",
    clauseReference: item.clauseReference ?? "",
    milestoneId: item.milestoneId ?? "",
    sowId: item.sowId ?? null,
    dueDate: item.dueDate ?? "",
    ragRating: item.ragRating ?? "",
    recurrenceRule: item.recurrenceRule
      ? { ...item.recurrenceRule }
      : undefined,
    status: item.status,
    evidenceLinks: item.evidenceLinks ? [...item.evidenceLinks] : [],
    notes: item.notes ?? "",
  };
}

/**
 * Build the backend Create payload from form state. Strips empty-string
 * optional fields so we don't persist blanks, and omits ragRating when
 * the user hasn't picked one. `sowId === null` is omitted here (the
 * `(none)` default on Create is the unattached case); the explicit
 * `null` clear-attachment signal is added by the Update-payload caller
 * when it knows the source had a sowId.
 *
 * `ctx.contractEndDate` is used as a fallback end date when the user
 * leaves the recurrence end date blank: the serialiser fills it in so
 * generated occurrences stop at the contract boundary by default.
 * A user-entered end date always takes precedence.
 */
export function toCreateDeliverableInput(
  state: DeliverableFormState,
  contractId: string,
  ctx?: { contractEndDate?: string },
): CreateDeliverableInput {
  const base: CreateDeliverableInput = {
    contractId,
    name: state.name.trim(),
    deliverableType: state.deliverableType,
    owner: state.owner,
    status: state.status,
  };
  if (state.description.trim()) base.description = state.description.trim();
  if (state.clauseReference.trim()) {
    base.clauseReference = state.clauseReference.trim();
  }
  if (state.milestoneId) base.milestoneId = state.milestoneId;
  if (state.sowId) base.sowId = state.sowId;
  if (state.dueDate) base.dueDate = state.dueDate;
  if (state.ragRating) base.ragRating = state.ragRating;
  if (state.recurrenceRule) {
    const rule = state.recurrenceRule;
    const endDate = rule.endDate ?? ctx?.contractEndDate;
    base.recurrenceRule = endDate ? { ...rule, endDate } : { ...rule };
  }
  if (state.evidenceLinks.length > 0) base.evidenceLinks = state.evidenceLinks;
  if (state.notes.trim()) base.notes = state.notes.trim();
  return base;
}

/**
 * Build the backend Update payload from form state, comparing against the
 * original deliverable to decide which fields to include.
 *
 * Field value contract:
 *   - `""` (empty string) in form state means "field not edited by the user"
 *     (e.g. RAG radio group initialised to empty when the field is hidden).
 *   - `null` means "user explicitly cleared the value" (e.g. sowId clear).
 *   - `undefined` (field omitted from payload) means "no change — leave the
 *     server value as-is".
 *
 * Mode-gated fields:
 * When `isRecurringParent` is `true`, the form hides per-occurrence fields
 * (ragRating, status, dueDate, milestoneId, evidenceLinks). The helper omits
 * those fields from the payload entirely so the server's stored values are
 * left untouched. Sending an empty-string ragRating to the server would clear
 * the occurrence's stored RAG even though the user never saw or touched it.
 *
 * Always-on fields: name, deliverableType, owner.
 *
 * Omit-when-blank fields: description, clauseReference, notes,
 * recurrenceRule, evidenceLinks. Empty in form state → field is absent from
 * the payload, backend leaves the stored value untouched. This mirrors
 * toCreateDeliverableInput and means there is no "clear via blank input"
 * affordance — to actually null out a stored value the caller must use a
 * dedicated clear signal (see buildSowIdUpdatePatch for the established
 * pattern).
 *
 * ragRating comparison logic (when the RAG field is visible):
 *   - state.ragRating !== original.ragRating → include new value (or "" to
 *     express "not set").
 *   - state.ragRating === original.ragRating → omit (avoids no-op writes
 *     and reduces noise in the audit log).
 */
export function toUpdateDeliverableInput(
  state: DeliverableFormState,
  original: Deliverable,
  isRecurringParent: boolean,
): Omit<UpdateDeliverableInput, "version"> {
  const patch: Omit<UpdateDeliverableInput, "version"> = {
    name: state.name.trim(),
    deliverableType: state.deliverableType,
    owner: state.owner,
  };

  if (state.description.trim()) {
    patch.description = state.description.trim();
  }

  if (state.clauseReference.trim()) {
    patch.clauseReference = state.clauseReference.trim();
  }

  if (state.notes.trim()) {
    patch.notes = state.notes.trim();
  }

  if (state.recurrenceRule) {
    patch.recurrenceRule = { ...state.recurrenceRule };
  }

  if (state.evidenceLinks.length > 0 && !isRecurringParent) {
    patch.evidenceLinks = state.evidenceLinks;
  }

  // Per-occurrence fields: omit entirely when the form is in parent mode
  // so hidden fields never overwrite the occurrence's stored values.
  //
  // `status` is deliberately NOT emitted: the legacy bare-`status` write path
  // has been removed and the backend rejects it. Deliverable state changes
  // flow through lifecycle events — the edit form always sends
  // `lifecycleEventsReplace` (the LifecycleLogEditor is the uniform edit
  // surface). State still tracks `status` for read-time RAG-lock derivation.
  if (!isRecurringParent) {
    if (state.dueDate) patch.dueDate = state.dueDate;

    if (state.milestoneId) patch.milestoneId = state.milestoneId;

    // Include ragRating only when it changed (avoids no-op writes).
    const originalRag = original.ragRating ?? "";
    const stateRag = state.ragRating;
    if (stateRag !== originalRag) {
      // stateRag === "" means user selected "Not set" — send the empty
      // string; the backend schema accepts optional ragRating so omitting
      // it is a no-op but sending "" could mean clearing. For the "Not set"
      // selection we omit rather than send "" to avoid unintended clears:
      // the server treats a missing field as no-change.
      if (stateRag !== "") {
        patch.ragRating = stateRag as RagRating;
      }
      // stateRag === "" and originalRag !== "" → user explicitly de-selected;
      // omit from payload (no-op, preserving server value). If the UI ever
      // needs an explicit null-clear signal for ragRating, add it here.
    }
    // stateRag === originalRag → unchanged → omit entirely.
  }

  return patch;
}

/**
 * Compute the `sowId` patch for an Update payload. Returns:
 *  - `{ sowId: <id> }` when state has a SOW and it differs from source;
 *  - `{ sowId: null }` when state is null and source had a SOW (clear);
 *  - `{}` (omit) when there's no change (idempotent PUT).
 *
 * Callers spread the result onto the Update payload built from
 * `toCreateDeliverableInput`. Without this helper, a clear-attachment
 * would silently no-op because `toCreateDeliverableInput` drops null.
 */
export function buildSowIdUpdatePatch(
  state: DeliverableFormState,
  source: Deliverable,
): { sowId?: string | null } {
  const before = source.sowId ?? null;
  const after = state.sowId;
  if (before === after) return {};
  return { sowId: after };
}
